// src/helpers/dropdownHelper.js
import { get } from './api_helper';

/**
 * Master Category Definitions & Human-Readable Labels for SuperAdmin & UI
 */
export const DROPDOWN_CATEGORIES = [
  { key: 'dental_chart_examination', label: 'Dental Chart Examination / Findings', icon: 'RiToothLine', description: 'Conditions and findings selectable in the Odontogram dental chart' },
  { key: 'treatment_category_type', label: 'Treatment Categories & Procedures', icon: 'RiStethoscopeLine', description: 'Clinical treatment categories and dental procedure types' },
  { key: 'rx_frequency', label: 'Prescription Frequencies', icon: 'RiTimeLine', description: 'Dosage intake frequency formats (e.g. 1-1-1, TDS, SOS)' },
  { key: 'rx_dosage', label: 'Prescription Dosages', icon: 'RiMedicineBottleLine', description: 'Medicine strength and volume measurements' },
  { key: 'rx_duration', label: 'Prescription Durations', icon: 'RiCalendarEventLine', description: 'Medicine intake course durations' },
  { key: 'auscultation', label: 'Auscultation Findings', icon: 'RiHeartPulseLine', description: 'Cardiovascular and respiratory auscultation notes' },
  { key: 'file_category', label: 'File & Document Categories', icon: 'RiFolder2Line', description: 'Patient record attachment categories and diagnostic tags' },
  { key: 'blood_group', label: 'Blood Groups', icon: 'RiDropLine', description: 'Patient ABO & Rh blood group classifications' },
  { key: 'salutation', label: 'Patient Salutations / Titles', icon: 'RiUser3Line', description: 'Formal name honorifics' },
  { key: 'gender', label: 'Gender Options', icon: 'RiGenderlessLine', description: 'Gender identification options' },
  { key: 'appointment_status', label: 'Appointment Statuses', icon: 'RiCalendarCheckLine', description: 'Operational states for clinical calendar appointments' },
  { key: 'referral_source', label: 'Patient Referral Sources', icon: 'RiShareForwardLine', description: 'Acquisition channels and marketing attribution tags' },
  { key: 'payment_mode', label: 'Payment Modes / Methods', icon: 'RiMoneyDollarCircleLine', description: 'Accepted tender and digital settlement channels' },
  { key: 'clinical_symptoms', label: 'Clinical Symptoms & Chief Complaints', icon: 'RiPulseLine', description: 'Standard symptoms and chief complaints selectable during consultation' },
];

/**
 * Resilient Default Fallbacks (used offline or on first render)
 */
export const DEFAULT_DROPDOWNS_FALLBACK = {
  dental_chart_examination: [
    { label: 'Tooth Investigation', value: 'tooth_investigation' },
    { label: 'Caries Surfaces Involved', value: 'Caries Surfaces Involved', extra_data: { tooth: 'caries' } },
    { label: 'Fracture', value: 'Fracture', extra_data: { tooth: 'selected' } },
    { label: 'Restoration', value: 'restoration', extra_data: { tooth: 'selected' } },
    { label: 'Endodontic', value: 'endodontic', extra_data: { tooth: 'selected' } },
    { label: 'Extraction', value: 'extraction', extra_data: { tooth: 'selected' } },
    { label: 'Denture', value: 'denture', extra_data: { tooth: 'selected' } },
    { label: 'Periapical Abscess', value: 'periapical_abscess', extra_data: { tooth: 'selected' } },
    { label: 'Gingival Recession', value: 'gingival_recession', extra_data: { tooth: 'selected' } },
  ],
  treatment_category_type: [
    { group_name: 'Diagnostic & Preventive Care', label: 'Dental Consultation', value: 'consultation' },
    { group_name: 'Diagnostic & Preventive Care', label: 'Dental Cleaning / Scaling', value: 'scaling' },
    { group_name: 'Restorative Treatments', label: 'Composite Filling', value: 'composite_filling' },
    { group_name: 'Restorative Treatments', label: 'Ceramic / Zirconia Crown', value: 'crowns' },
    { group_name: 'Root Canal / Endodontics', label: 'Root Canal Treatment (RCT)', value: 'rct' },
    { group_name: 'Prosthodontics (Replacement of Teeth)', label: 'Dental Implant Placement', value: 'implant' },
    { group_name: 'Prosthodontics (Replacement of Teeth)', label: 'Fixed Dental Bridge', value: 'bridge' },
    { group_name: 'Prosthodontics (Replacement of Teeth)', label: 'Complete Denture', value: 'complete_denture' },
    { group_name: 'Orthodontics (Braces & Alignment)', label: 'Clear Aligners / Ortho', value: 'aligner' },
    { group_name: 'Oral Surgery', label: 'Simple Tooth Extraction', value: 'simple_extraction' },
    { group_name: 'Cosmetic Dentistry', label: 'Teeth Whitening & Bleaching', value: 'whitening' },
  ],
  rx_frequency: [
    { value: '1-0-0', label: '1-0-0 (Morning Only)' },
    { value: '1-1-0', label: '1-1-0 (Morning & Afternoon)' },
    { value: '1-0-1', label: '1-0-1 (Morning & Night)' },
    { value: '0-1-0', label: '0-1-0 (Afternoon Only)' },
    { value: '0-1-1', label: '0-1-1 (Afternoon & Night)' },
    { value: '0-0-1', label: '0-0-1 (Night Only - Bedtime)' },
    { value: '1-1-1', label: '1-1-1 (TDS - 3 Times Daily)' },
    { value: '1-1-1-1', label: '1-1-1-1 (QID - 4 Times Daily)' },
    { value: 'SOS', label: 'SOS (As Needed / In Pain)' },
  ],
  rx_dosage: [
    { value: '100mg', label: '100mg' },
    { value: '200mg', label: '200mg' },
    { value: '250mg', label: '250mg' },
    { value: '500mg', label: '500mg' },
    { value: '650mg', label: '650mg' },
    { value: '1000mg', label: '1000mg' },
    { value: '1ml', label: '1ml' },
    { value: '2ml', label: '2ml' },
    { value: '5ml', label: '5ml (1 Teaspoon)' },
    { value: '10ml', label: '10ml (2 Teaspoons)' },
    { value: '15ml', label: '15ml' },
  ],
  rx_duration: [
    { value: '1 day', label: '1 Day' },
    { value: '2 days', label: '2 Days' },
    { value: '3 days', label: '3 Days' },
    { value: '5 days', label: '5 Days' },
    { value: '7 days', label: '7 Days' },
    { value: '10 days', label: '10 Days' },
    { value: '14 days', label: '14 Days' },
    { value: '1 month', label: '1 Month' },
    { value: 'Till Next Visit', label: 'Till Next Visit' },
  ],
  auscultation: [
    { value: 'Normal', label: 'Normal' },
    { value: 'Murmur', label: 'Murmur' },
    { value: 'Gallop', label: 'Gallop' },
    { value: 'Rub', label: 'Rub' },
  ],
  file_category: [
    { value: '1', label: 'Testimonials & Feedback', extra_data: { color: 'info' } },
    { value: '2', label: 'Scanned X-Ray / OPG / IOPA / CBCT', extra_data: { color: 'primary' } },
    { value: '3', label: 'Diagnostic & Lab Reports', extra_data: { color: 'success' } },
    { value: '4', label: 'Other Clinical Documents', extra_data: { color: 'secondary' } },
  ],
  blood_group: [
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' },
    { value: 'Unknown', label: 'Unknown / Not Tested' },
  ],
  salutation: [
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Master', label: 'Master' },
    { value: 'Baby', label: 'Baby' },
  ],
  gender: [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ],
  appointment_status: [
    { value: '0', label: 'Scheduled', extra_data: { color: 'primary' } },
    { value: '1', label: 'Confirmed', extra_data: { color: 'success' } },
    { value: '2', label: 'In Consultation', extra_data: { color: 'warning' } },
    { value: '3', label: 'Completed', extra_data: { color: 'success' } },
    { value: '4', label: 'Cancelled', extra_data: { color: 'danger' } },
    { value: '5', label: 'No Show', extra_data: { color: 'secondary' } },
  ],
  referral_source: [
    { value: 'Walk-in', label: 'Walk-in / Direct' },
    { value: 'Google', label: 'Google Search / Website' },
    { value: 'Social Media', label: 'Social Media (Instagram / FB)' },
    { value: 'Doctor Referral', label: 'Doctor Referral' },
    { value: 'Friend/Family', label: 'Friend / Family Recommendation' },
    { value: 'Camp/School', label: 'Dental Health Camp / School' },
    { value: 'Other', label: 'Other' },
  ],
  payment_mode: [
    { value: 'Cash', label: 'Cash' },
    { value: 'UPI / QR', label: 'UPI / QR (GPay, PhonePe, Paytm)' },
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'Debit Card', label: 'Debit Card' },
    { value: 'Net Banking', label: 'Net Banking' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Insurance', label: 'Insurance / TPA Claim' },
  ],
};

// In-memory cache for fast lookups
let cachedDropdowns = null;
let cachePromise = null;

/**
 * Fetch all system dropdowns from backend with fallback
 */
export const fetchAllDropdowns = async (forceRefresh = false) => {
  if (cachedDropdowns && !forceRefresh) {
    return cachedDropdowns;
  }

  if (cachePromise && !forceRefresh) {
    return await cachePromise;
  }

  cachePromise = (async () => {
    try {
      const res = await get('/dropdowns/all?grouped=true');
      if (res?.success && res.body) {
        cachedDropdowns = res.body;
        return cachedDropdowns;
      }
    } catch (err) {
      console.warn('System dropdowns API fetch failed, using fallback:', err);
    }
    cachedDropdowns = DEFAULT_DROPDOWNS_FALLBACK;
    return cachedDropdowns;
  })();

  return await cachePromise;
};

/**
 * Invalidate dropdown cache (e.g. after SuperAdmin updates)
 */
export const invalidateDropdownCache = () => {
  cachedDropdowns = null;
  cachePromise = null;
};

/**
 * Get dropdown options for a specific category (sync with fallback or async fetch)
 */
export const getDropdownOptions = async (category) => {
  const all = await fetchAllDropdowns();
  return all[category] || DEFAULT_DROPDOWNS_FALLBACK[category] || [];
};

/**
 * Get grouped options format (e.g. for react-select grouped categories)
 */
export const getGroupedDropdownOptions = async (category) => {
  const options = await getDropdownOptions(category);
  const groups = {};
  const ungrouped = [];

  options.forEach(opt => {
    if (opt.group_name) {
      if (!groups[opt.group_name]) groups[opt.group_name] = [];
      groups[opt.group_name].push({
        label: opt.label,
        value: opt.value,
        extra_data: opt.extra_data,
      });
    } else {
      ungrouped.push({
        label: opt.label,
        value: opt.value,
        extra_data: opt.extra_data,
      });
    }
  });

  const result = Object.entries(groups).map(([groupName, groupOptions]) => ({
    label: groupName,
    options: groupOptions,
  }));

  if (ungrouped.length > 0) {
    result.push({
      label: 'General / Other',
      options: ungrouped,
    });
  }

  return result;
};
